import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { firestoreProd } from "../../firebase";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./Changelogs.css";

// Views the `prodDeployments` collection. Each deployment run creates a doc
// there (from ProdDeployment/page.jsx) with its label, PRs, issues, jira
// tickets, and both internal + client-facing changelog markdown. This is the
// source of truth — the Prod Deployment flow writes to it directly, so every
// run shows up here regardless of whether the backend's redundant
// `prodChangelogs` persist ran.

const CHANGELOGS_COLLECTION = "prodDeployments";

function toJsDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatChangelogDate(value) {
  const d = toJsDate(value);
  if (!d) return "—";
  return d.toLocaleString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function ChangelogsPage({ onReady }) {
  // Optional /changelogs/:id — a deep link straight to one deployment's
  // changelog (e.g. the URL the backend posts to Slack after a prod deploy).
  const { id: urlId } = useParams();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(urlId || null);
  const [view, setView] = useState("complete"); // "complete" | "client"
  const [searchTerm, setSearchTerm] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(
          collection(firestoreProd, CHANGELOGS_COLLECTION),
          orderBy("createdAt", "desc"),
        ),
      );
      const list = snap.docs
        .map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            ticketCount:
              typeof data.ticketCount === "number"
                ? data.ticketCount
                : Array.isArray(data.jiraTickets)
                  ? data.jiraTickets.length
                  : null,
          };
        })
        .filter((e) => (e.changelogComplete || "").trim() || (e.changelogClient || "").trim());
      setEntries(list);
      // Prefer the URL's :id when it resolves to a real entry; otherwise fall
      // back to the most recent one, same as before this route took a param.
      const urlIdIsValid = urlId && list.some((e) => e.id === urlId);
      if (urlIdIsValid && selectedId !== urlId) {
        setSelectedId(urlId);
      } else if (list.length > 0 && !selectedId) {
        setSelectedId(list[0].id);
      }
    } catch (err) {
      console.error("Failed to load changelogs:", err);
      toast.error(`Failed to load changelogs: ${err.message}`);
    } finally {
      setLoading(false);
      onReady?.();
    }
  }, [urlId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const filteredEntries = entries.filter((e) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      e.id.toLowerCase().includes(q) ||
      (e.label || "").toLowerCase().includes(q) ||
      (e.changelogComplete || "").toLowerCase().includes(q) ||
      (e.changelogClient || "").toLowerCase().includes(q)
    );
  });

  const selected = entries.find((e) => e.id === selectedId);
  const selectedMarkdown = selected
    ? (view === "complete" ? selected.changelogComplete : selected.changelogClient)
    : "";

  const copyToClipboard = () => {
    if (!selectedMarkdown) return;
    navigator.clipboard.writeText(selectedMarkdown);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="ChangelogsPageContainer">
      <div className="container-fluid py-4 px-4">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme="light"
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <h1 className="mb-1">Changelogs</h1>
      <p className="text-muted mb-4">
        Production release notes — one entry per deployment, sourced from the Prod Deployment flow.
      </p>

      <div className="changelogs-tab">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h5 className="mb-1">Production Changelogs</h5>
            <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
              All changelogs generated from the Prod Deployment flow, one entry per deployment.
              Both internal and client-facing versions are stored.
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search date, label, or content…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ minWidth: 260 }}
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={load}
              disabled={loading}
            >
              {loading ? "Refreshing…" : "↻ Refresh"}
            </button>
          </div>
        </div>

        {loading && entries.length === 0 ? (
          <div className="d-flex align-items-center gap-2 text-muted py-4">
            Loading changelogs…
          </div>
        ) : entries.length === 0 ? (
          <div className="changelogs-empty">
            <div style={{ fontSize: "2.5rem" }}>📋</div>
            <div className="fw-semibold mt-2">No changelogs yet</div>
            <div className="text-muted" style={{ fontSize: "0.85rem" }}>
              Run the Prod Deployment flow → Release Automation to generate the first one.
            </div>
          </div>
        ) : (
          <div className="changelogs-layout">
            <div className="changelogs-sidebar">
              {filteredEntries.length === 0 ? (
                <div className="text-muted small p-2">No matches for "{searchTerm}"</div>
              ) : (
                filteredEntries.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className={`changelogs-sidebar-item ${selectedId === entry.id ? "active" : ""}`}
                    onClick={() => setSelectedId(entry.id)}
                  >
                    <div className="changelogs-sidebar-date">
                      {formatChangelogDate(entry.createdAt || entry.updatedAt)}
                    </div>
                    <div className="changelogs-sidebar-meta">
                      {entry.label && <span className="changelogs-sidebar-label">{entry.label}</span>}
                      {typeof entry.ticketCount === "number" && (
                        <span className="changelogs-sidebar-count">
                          {entry.ticketCount} ticket{entry.ticketCount !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="changelogs-content">
              {!selected ? (
                <div className="text-muted p-3">Select a date from the left to view the changelog.</div>
              ) : (
                <>
                  <div className="changelogs-content-header">
                    <div>
                      <div className="changelogs-content-date">
                        {formatChangelogDate(selected.createdAt || selected.updatedAt)}
                      </div>
                      <div className="changelogs-content-label">
                        {selected.label || "—"}
                        {typeof selected.ticketCount === "number" && (
                          <span className="ms-2 text-muted">
                            · {selected.ticketCount} ticket{selected.ticketCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className={`btn btn-sm ${view === "complete" ? "btn-primary" : "btn-outline-primary"}`}
                          onClick={() => setView("complete")}
                        >
                          Internal
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${view === "client" ? "btn-primary" : "btn-outline-primary"}`}
                          onClick={() => setView("client")}
                        >
                          Client-facing
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={copyToClipboard}
                        disabled={!selectedMarkdown}
                      >
                        Copy Markdown
                      </button>
                    </div>
                  </div>

                  <div className="changelogs-markdown">
                    {selectedMarkdown ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        linkTarget="_blank"
                      >
                        {selectedMarkdown}
                      </ReactMarkdown>
                    ) : (
                      <div className="text-muted small">
                        No {view === "complete" ? "internal" : "client-facing"} changelog stored for this deployment.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
